import { ColumnDef } from '@tanstack/react-table';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button.tsx';
import { Area, AreaChart } from 'recharts';
import { formatDistance } from 'date-fns';
import { isValidDate } from '@/utils/utils.ts';
import { getDeviceFieldByCode } from '@/features/devices/schemas/devices.metadata.ts';
import { TUserRoles } from '@/features/users/schemas/users.schema.ts';
import { PlantDeviceFields } from '@/features/plants/hooks/queries/[plantId]/use-get-plant-devices-overview.ts';

export const tableColumns = (
  userRole?: TUserRoles
): (ColumnDef<PlantDeviceFields> & { label?: string })[] => [
  {
    size: 60,
    id: 'expander',
    header: '',
    label: '',
    cell: ({ row }) => {
      return row.getCanExpand() ? (
        <Button
          className="w-full p-2 sm:w-min"
          variant="secondary"
          {...{
            onClick: row.getToggleExpandedHandler(),
            style: { cursor: 'pointer' },
          }}
        >
          {row.getIsExpanded() ? <ChevronDown /> : <ChevronRight />}
        </Button>
      ) : (
        '🔵'
      );
    },
  },
  {
    size: 300,
    header: 'Name',
    label: 'Name',
    accessorKey: 'fieldCode',
    accessorFn: (row) => {
      const label = getDeviceFieldByCode(row.deviceTypeCode, row.fieldCode)?.name;

      return label ? label : row.fieldCode;
    },
    cell: ({ row, getValue }) => {
      const showTitle =
        userRole === 'Company administrator' ||
        userRole === 'Company owner' ||
        userRole === 'Installer';

      return (
        <div
          className="text-nowrap"
          style={{
            paddingLeft: `${row.depth * 4}rem`,
          }}
          title={
            showTitle
              ? `Device Type Code: ${row.original.deviceTypeCode} \nField Code: ${row.original.fieldCode}`
              : undefined
          }
        >
          {getValue<string>()}
        </div>
      );
    },
    footer: (props) => props.column.id,
  },
  {
    size: 300,
    id: 'value',
    label: 'Value',
    accessorFn: (row) => {
      if (!row.value && row.value !== 0) return '--';

      const field = getDeviceFieldByCode(row.deviceTypeCode, row.fieldCode);

      if (field?.unit !== '') {
        return Number(row.value)?.toFixed(field?.decimals);
      }

      return row.value;
    },
    cell: ({ row, getValue }) => (
      <div
        className="font-semibold sm:text-end"
        style={{
          paddingLeft: `${row.depth * 2}rem`,
          // textAlign: 'end',
        }}
      >
        {getValue<string>()}
      </div>
    ),
    header: () => <div className="text-end">Value</div>,

    footer: (props) => props.column.id,
  },
  {
    id: 'unit',
    label: 'Unit',
    accessorFn: (row) => getDeviceFieldByCode(row.deviceTypeCode, row.fieldCode)?.unit,
    header: 'Unit',
    footer: (props) => props.column.id,
  },
  {
    id: 'chart',
    label: 'Chart',
    header: 'Last 24 hours',
    accessorFn: (row) => row.last24Hours,
    cell: ({ getValue }) => {
      return (
        <AreaChart width={200} height={37} data={getValue<PlantDeviceFields[]>()}>
          <Area
            type="monotone"
            dataKey="value"
            stroke="#8884d8"
            fill="#8884d820"
            strokeWidth={1.5}
          />
        </AreaChart>
      );
    },
    footer: (props) => props.column.id,
  },
  {
    size: 200,
    id: 'date',
    label: 'Last updated',
    header: 'Last updated',
    accessorFn: (row) => {
      if (!row.value && row.value !== 0) return '--';

      if (isValidDate(row.time)) {
        return formatDistance(row.time, new Date(), {
          addSuffix: true,
        });
      }

      return formatDistance(new Date(), new Date(), {
        addSuffix: true,
      });
    },
    cell: ({ getValue }) => <div className="text-nowrap">{getValue<string>()}</div>,
    footer: (props) => props.column.id,
  },
];
